import { Box } from "@mui/material";
import { MunicipalityAutocomplete } from "Components/Shared/Inputs/MunicipalityAutocomplete";
import { FilterDropdown } from "Components/Shared/Components/FilterDropdown";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { useSearchParams } from "Navigation/useSearchParams";
import { useFacilityCategoryList } from "Hooks/Options/useFacilityCategoryList";
import {
  CATEGORY_PARAM,
  MUNICIPALITY_PARAM,
  FILTER_PARAMS,
} from "Components/WhereToPay/StoresSection/StoreParams";

const RESOURCES = Resources.Pages.Content.WhereToPay.StoresSection;

export const StoreFilters = () => {
  // Temporary UI state for municipality input (used for backend search, not stored in URL)
  const [municipalityInputValue, setMunicipalityInputValue] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useResource();

  // Read single values directly from URL params (only one category and one municipality at a time)
  // searchParams.get() returns the first value if multiple exist, ensuring single selection
  const categoryFilter = searchParams.get(CATEGORY_PARAM) || "";
  const municipalityFilter = searchParams.get(MUNICIPALITY_PARAM) || "";

  const updateCategoryFilter = (value: string | number | "") => {
    const params: Record<string, string> = {};

    // Preserve existing params (excluding filter params and page - reset to page 1 on filter change)
    searchParams.forEach((paramValue, key) => {
      if (!FILTER_PARAMS.includes(key)) {
        params[key] = paramValue;
      }
    });

    // Set single category value (replaces any existing category)
    if (value && value !== "") {
      params[CATEGORY_PARAM] = String(value);
    }
    // If empty, category param is omitted (filter cleared)

    // Preserve municipality param (single value)
    if (municipalityFilter && municipalityFilter !== "") {
      params[MUNICIPALITY_PARAM] = String(municipalityFilter);
    }

    setSearchParams(params);
  };

  const updateMunicipalityFilter = (value: string | number | "") => {
    const params: Record<string, string> = {};

    // Preserve existing params (excluding filter params and page - reset to page 1 on filter change)
    searchParams.forEach((paramValue, key) => {
      if (!FILTER_PARAMS.includes(key)) {
        params[key] = paramValue;
      }
    });

    // Preserve category param (single value)
    if (categoryFilter && categoryFilter !== "") {
      params[CATEGORY_PARAM] = String(categoryFilter);
    }

    // Set single municipality value (replaces any existing municipality)
    if (value && value !== "") {
      params[MUNICIPALITY_PARAM] = String(value);
    } else {
      // Clear input value when filter is cleared
      setMunicipalityInputValue("");
    }
    // If empty, municipality param is omitted (filter cleared)

    setSearchParams(params);
  };
  const { categoryOptions: facilityCategoryOptions } =
    useFacilityCategoryList();

  return (
    <Box width="100%" display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
      <FilterDropdown
        label={t(RESOURCES.CategoryLabel)}
        options={facilityCategoryOptions}
        value={categoryFilter}
        onChange={updateCategoryFilter}
        placeholder={t(RESOURCES.CategoryPlaceholder)}
        accessibilityLabel={t(RESOURCES.CategoryAccessibility)}
      />
      <MunicipalityAutocomplete
        label={t(RESOURCES.MunicipalityLabel)}
        value={municipalityFilter}
        inputValue={municipalityInputValue}
        placeholder={t(RESOURCES.MunicipalityPlaceholder)}
        accessibilityLabel={t(RESOURCES.MunicipalityAccessibility)}
        onChange={(value) => {
          updateMunicipalityFilter(value);
          if (value === "") {
            setMunicipalityInputValue("");
          }
        }}
        onInputChange={(value) => {
          setMunicipalityInputValue(value);
          if (!value) {
            updateMunicipalityFilter("");
          }
        }}
      />
    </Box>
  );
};
